import React from 'react'
import { icons } from 'lucide-react'
import { AreaIcon } from '../../icons/AreaIcon'
import { getRewardIconUrl } from '../../../lib/data/rewardCatalog'
import { parseProjectIcon, type ProjectIconKind } from '../../../lib/projectIcon/projectIcon'
import { isIconColorKey } from '../../../lib/projectIcon/titlePalette'
import './ProjectIcon.scss'

export interface ProjectIconProps {
  /** `Project.icon` — `happy:…`, `emoji:…` oder `lucide:…`; leer = Standard-Icon. */
  icon: string | null | undefined
  /** `Project.iconColor` — färbt nur Lucide-Icons; `null` = Ton des Umfelds. */
  iconColor?: string | null
  /** Kantenlänge des Felds in px. Das Zeichen darin wächst je nach Art unterschiedlich weit (siehe `glyphSize`). */
  size: number
  /** Was ohne Icon steht. Standard: das Projekt-Icon der App (`AreaIcon`). */
  fallback?: React.ReactNode
  className?: string
}

/**
 * Wie groß das Zeichen im Feld steht. Happy Emojis füllen es — sie haben Luft
 * im Bild. Emojis und Lucide-Linien wirken bei gleicher Kante größer und
 * stehen deshalb kleiner (entschieden 2026-09-25: Sidebar 26 → 18/16,
 * Kopf 64 → 56/48). Bis 20 px steht alles in voller Größe.
 */
export function glyphSize(kind: ProjectIconKind, size: number): number {
  if (kind === 'happy' || size <= 20) return size
  if (size >= 48) return Math.round(size * (kind === 'emoji' ? 0.875 : 0.75))
  return kind === 'emoji' ? Math.round(size * 0.7) : 16
}

/** Das Lucide-Icon zu einem gespeicherten Namen (`icons`-Schlüssel, z. B. `Rocket`). */
export function lucideIconByName(name: string) {
  return (icons as Record<string, (typeof icons)[keyof typeof icons]>)[name] ?? null
}

/**
 * Das **Projekt-Icon** (CONTEXT.md „Projekt-Kopf“) — überall, wo ein
 * Projektname mit Icon steht: Sidebar, Tab, Projekt-Schild, Menüs, Kopf.
 *
 * Emojis kommen aus der Systemschrift und stehen in einem SVG-`<text>`: So
 * folgt ihre Größe dem `size`-Attribut, ohne Inline-Style.
 *
 * @example
 * <ProjectIcon icon={project.icon} iconColor={project.iconColor} size={26} />
 * <ProjectIcon icon="emoji:🚀" size={64} />
 */
export const ProjectIcon: React.FC<ProjectIconProps> = ({ icon, iconColor, size, fallback, className }) => {
  const parsed = parseProjectIcon(icon)
  const classes = (kind: string) => `project-icon project-icon--${kind}${className ? ` ${className}` : ''}`

  if (parsed?.kind === 'happy') {
    const src = getRewardIconUrl(parsed.value, size > 64 ? 256 : 64)
    if (src) {
      return (
        <span className={classes('happy')} data-size={size}>
          <img className="project-icon__image" src={src} width={size} height={size} alt="" draggable={false} />
        </span>
      )
    }
  }

  if (parsed?.kind === 'emoji') {
    const glyph = glyphSize('emoji', size)
    return (
      <span className={classes('emoji')}>
        <svg className="project-icon__emoji" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={glyph * 0.86}>
            {parsed.value}
          </text>
        </svg>
      </span>
    )
  }

  if (parsed?.kind === 'lucide') {
    const Lucide = lucideIconByName(parsed.value)
    if (Lucide) {
      return (
        <span className={classes('lucide')} data-color={isIconColorKey(iconColor) ? iconColor : undefined}>
          <Lucide className="project-icon__lucide" size={glyphSize('lucide', size)} />
        </span>
      )
    }
  }

  return <>{fallback ?? <AreaIcon size={Math.min(size, 16)} />}</>
}
